// src/pages/Plans.tsx
import { useMemo, useState } from "react";
import CTAButton from "../components/CTAButton";
import NavBar from "../components/NavBar";
import UserAvatar from "../components/UserAvatar";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { dict, getInitialLang, type Lang } from "../lib/i18n";
import {
  initTG,
  getAvatarUrl,
  getUserName,
  vibrate,
  vibrateSuccess,
} from "../lib/telegram";
import { createStarsInvoice } from "../lib/api";
import MobileNav from "../components/MobileNav";

type PlanCode = "starter" | "walk" | "best";

const plans = [
  {
    code: "starter" as PlanCode,
    name: "Старт пакет",
    price: "250 ⭐",
    bullets: [
      "100 текстовых запросов в день",
      "5 голосовых запросов в день",
      "+3 изображения в день",
      "10 генераций в неделю",
    ],
  },
  {
    code: "walk" as PlanCode,
    name: "На ход ноги",
    price: "370 ⭐",
    bullets: [
      "320 текстовых запросов в день",
      "15 голосовых запросов в день",
      "+4 изображения в день",
      "17 генераций в неделю",
    ],
  },
  {
    code: "best" as PlanCode,
    name: "Лучший из лучших",
    price: "430 ⭐",
    bullets: [
      "5000 текстовых запросов в день",
      "60 голосовых запросов в день",
      "+10 изображений в день",
      "30 генераций в неделю",
    ],
  },
];

export default function Plans() {
  const [lang] = useState<Lang>(() => getInitialLang(null));

  const tg = useMemo(() => initTG(), []);
  const tgId = tg?.initDataUnsafe?.user?.id ?? null;

  const name = getUserName();
  const avatar = getAvatarUrl();

  const [loadingPlan, setLoadingPlan] = useState<PlanCode | null>(null);

  async function handleChoose(code: PlanCode) {
    if (!tgId || !tg) {
      alert("Оплата доступна только в Telegram Mini App.");
      return;
    }

    vibrate();
    setLoadingPlan(code);

    try {
      const { invoice_url } = await createStarsInvoice(code, tgId);

      const w = tg as any;

      if (w?.openInvoice) {
        w.openInvoice(invoice_url, (status: string) => {
          if (status === "paid") vibrateSuccess();
        });
      } else if (w?.openTelegramLink) {
        w.openTelegramLink(invoice_url);
      } else {
        window.open(invoice_url, "_blank");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPlan(null);
    }
  }

  return (
    <div className="min-h-screen-svh bg-grid relative text-white">
      <div className="absolute inset-0 grid-lines pointer-events-none" />

      {/* HEADER */}
      <header className="relative z-10 max-w-6xl mx-auto h-12 md:h-16 flex items-center justify-between px-3 md:px-4 safe-top">
        <div className="flex items-center gap-2">
          <UserAvatar name={name} src={avatar ?? undefined} />
          <span className="text-xs md:text-sm text-white/70">
            {dict[lang].welcome} {name}
          </span>
        </div>

        <div className="hidden md:flex">
          <NavBar />
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher value={lang} onToggle={() => {}} />
        </div>
      </header>

      {/* MOBILE NAV */}
      <MobileNav>
        <NavBar compact />
      </MobileNav>

      {/* MAIN */}
      <main className="relative z-10 max-w-6xl mx-auto px-3 md:px-4 pt-6 md:pt-10 pb-8">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">Подписки</h1>
        <p className="text-white/60 text-sm md:text-base mb-6">
          Оплачивайте подписки звёздами внутри Telegram.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {plans.map((p) => {


            return (
              <article
                key={p.code}
                className="glass rounded-2xl p-4 md:p-5 flex flex-col justify-between border border-white/10 bg-white/5"
              >
                <div>
                  <div className="flex items-baseline justify-between gap-2 mb-2">
                    <h2 className="text-lg md:text-xl font-semibold">{p.name}</h2>
                    <span className="text-sm md:text-base text-amber-300">
                      {p.price}
                    </span>
                  </div>

                  <ul className="text-xs md:text-sm text-white/70 space-y-1.5 mt-3">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex gap-2">
                        <span className="mt-[3px] text-emerald-400">•</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-4">
                  <CTAButton
                    onClick={() => handleChoose(p.code)}

                  >
                    {loadingPlan === p.code ? "Создаём счёт…" : "Выбрать"}
                  </CTAButton>

                  {!tgId && (
                    <p className="text-[11px] text-white/50 mt-1 text-center">
                      Чтобы оплатить звёздами — открой через Telegram.
                    </p>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </main>
    </div>
  );
}
